import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeprecatedFAIcon } from '@components-library/Icons/DeprecatedFAIcon';
import { SvgIcon } from '@components-library/Icons/SvgIcon';
import { CopyIcon } from '@components-library/Icons/iconsComponents';
import { DEPRECATED_FA_GLOBAL_SPRITE } from '@components-library/Icons/interfaces';
import { InputFieldWrapper } from '@components-shared/forms/FieldLabel';
import { KeyMapKeys } from '@locales/lokalise/keyMap';
import { copyToClipboardTest } from '@utils/helper';
import ShowIcon from 'assets/icons/show icon.svg';
import cn from 'classnames';
import { AppContext } from 'contextStore/context';
import { Attributes } from '../formField.interface';

export interface IInputProps {
  key: string;
  name: string;
  title: string;
  type?: string;
  value: string;
  limit?: number;
  hintMessage?: string;
  required?: boolean;
  className?: string;
  tooltip?: string;
  copy?: boolean;
  hideLimitValue?: boolean;
  hideShowPassword?: boolean;
  onChange: (name: string, value: string) => void;
  onBlur?: () => void;
  valid?: boolean;
  validationMessage?: string;
  placeholders?: Record<string, string>;
  attributes?: Pick<Attributes, 'autoComplete' | 'disabled' | 'usePasswordStrengthMeterContext'>;
  placeholder?: string;
  renderAfter?: React.ReactNode;
}

export const Input = (props: IInputProps) => {
  const { i18n } = useTranslation();
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const { documentDirection } = useContext(AppContext);

  const {
    name,
    title,
    type,
    valid,
    validationMessage,
    placeholders,
    value,
    hintMessage,
    required,
    className,
    tooltip,
    limit,
    hideLimitValue,
    placeholder,
    copy,
    hideShowPassword = false,
  } = props;

  const { usePasswordStrengthMeterContext: _, ...attributes } = props.attributes ?? {};

  const onBlurHandler = () => {
    const { onBlur } = props;

    if (onBlur) {
      onBlur();
    }
  };

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { onChange } = props;
    const {
      target: { name, value },
    } = event;

    onChange(name, value);
  };

  const calculateInputStyle = () => {
    const defaultPadding = 8;
    const charWidth = 7;
    const inputPadding = limit ? defaultPadding + limit.toString().length * charWidth : defaultPadding;

    if (documentDirection === 'rtl') {
      return limit && !hideLimitValue ? { paddingLeft: inputPadding } : {};
    } else {
      return limit && !hideLimitValue ? { paddingRight: inputPadding } : {};
    }
  };

  const renderInput = () => {
    const inputPlaceholder = placeholder ? i18n.t(placeholder as KeyMapKeys) : '';

    return (
      <input
        className={cn('form__input', { invalid: !valid })}
        style={calculateInputStyle()}
        type={(!showPassword && type) || 'text'}
        name={name}
        onChange={onChangeHandler}
        value={value}
        {...attributes}
        onBlur={onBlurHandler}
        placeholder={inputPlaceholder}
        maxLength={limit}
      />
    );
  };

  const renderInputButtons = () => {
    return (
      <>
        {type === 'password' && !hideShowPassword && (
          <button
            type="button"
            tabIndex={1000}
            className={cn('form__showpassword', { hide: showPassword })}
            onClick={() => setShowPassword(!showPassword)}>
            <SvgIcon src={ShowIcon} className="form__showpassword-icon" />
          </button>
        )}

        {type === 'search' && (
          <DeprecatedFAIcon className="form__search-icon" icon={DEPRECATED_FA_GLOBAL_SPRITE.search} />
        )}

        {copy && (
          <button
            type="button"
            title="Copy"
            disabled={!value}
            tabIndex={1000}
            className="form__showpassword copy-btn"
            onClick={() => copyToClipboardTest(value)}>
            <CopyIcon size={16} />
          </button>
        )}
      </>
    );
  };

  const renderRemainingLimit = () => {
    if (!limit) {
      return null;
    }

    const remainingChars = value !== null ? limit - value.length : limit;

    return <span className="form__input-limit">{remainingChars}</span>;
  };

  const renderInputWithLimit = () => {
    if (limit && !hideLimitValue) {
      return (
        <div className="form__field-group">
          {renderRemainingLimit()}
          {renderInput()}
        </div>
      );
    } else {
      return renderInput();
    }
  };

  if (type === 'hidden') {
    return (
      <div key={name} data-fieldname={name}>
        <input type="hidden" value={value} />
      </div>
    );
  }

  return (
    <InputFieldWrapper
      name={name}
      title={title}
      className={className}
      tooltip={tooltip}
      placeholders={placeholders}
      valid={valid}
      hintMessage={hintMessage}
      required={required}
      validationMessage={validationMessage}
      renderAfter={props.renderAfter}>
      <div className="form__input-wrapper">
        {renderInputWithLimit()}
        {renderInputButtons()}
      </div>
    </InputFieldWrapper>
  );
};
